import {
  Button,
  createStyles,
  Grid,
  Tooltip,
  Typography,
  withStyles,
  type WithStyles,
} from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import { useTranslations } from "adapters/i18n";
import { DatePicker, type MaterialUiPickersDate } from "material-ui-pickers";
import React, { CSSProperties } from "react";
import { DATE_FORMATS, DateUtils } from "shared/dateUtils";
import { ConciliationDateFormState } from "../category.types";
import {
  CONCILIATION_DATE_PICKER_FORMAT,
  MIN_CONCILIATION_DATE,
} from "../utils/category.utils";
import { useEditConciliationDateForm } from "../hooks/useEditConciliationDateForm";
import EditConciliationDateModal from "./editConciliationDateModal";

type ConciliationDateFormProps = {
  conciliationStartDate: string | null;
  updateConciliationDate: (date: string) => Promise<void>;
} & WithStyles<typeof styles>;

function ConciliationDateForm({
  classes,
  conciliationStartDate,
  updateConciliationDate,
}: ConciliationDateFormProps) {
  const { t } = useTranslations();

  const {
    formState,
    handleChangeDate,
    handleCloseConfirmModal,
    handleOpenConfirmModal,
    handleSubmit,
    handleToggleDateError,
    isConfirmModalOpen,
    isEditingDate,
  } = useEditConciliationDateForm({
    conciliationStartDate,
    updateConciliationDate,
  });

  const handleClickButton = () => {
    if (isEditingDate) {
      handleOpenConfirmModal();
      return;
    }

    handleSubmit();
  };

  return (
    <Grid>
      <Typography className={classes.title} gutterBottom={true}>
        {t("conciliation:insert-conciliation-start-date")}
      </Typography>

      <div className={classes.formContainer}>
        <ConciliationDatePicker
          classes={classes}
          formState={formState}
          onChange={handleChangeDate}
          onError={handleToggleDateError}
        />
        <FormButton
          classes={classes}
          formState={formState}
          isEditing={isEditingDate}
          onClick={handleClickButton}
        />
      </div>

      <EditConciliationDateModal
        isOpen={isConfirmModalOpen}
        onClose={handleCloseConfirmModal}
        onConfirm={handleSubmit}
      />
    </Grid>
  );
}

type ConciliationDatePickerProps = {
  formState: ConciliationDateFormState;
  onChange: (value: string | null) => void;
  onError: (hasError: boolean) => void;
} & WithStyles<typeof styles>;

function ConciliationDatePicker({
  classes,
  formState,
  onChange,
  onError,
}: ConciliationDatePickerProps) {
  const { t } = useTranslations();

  const minDate = DateUtils.parse(
    MIN_CONCILIATION_DATE,
    DATE_FORMATS["DD/MM/YYYY"]
  ).format(CONCILIATION_DATE_PICKER_FORMAT);
  const maxDate = DateUtils.now().format(CONCILIATION_DATE_PICKER_FORMAT);

  const minDateMessage = t("common:validation.min-date", {
    date: MIN_CONCILIATION_DATE,
  });

  const mask = [/\d/, /\d/, "/", /\d/, /\d/, "/", /\d/, /\d/, /\d/, /\d/];

  const handleChangeDate = (date: MaterialUiPickersDate) => {
    if (date == null || !date.isValid()) {
      onChange(null);
      return;
    }

    onChange(date.format(CONCILIATION_DATE_PICKER_FORMAT));
  };

  const handleDateError = (newValue: MaterialUiPickersDate) => {
    onError(newValue?.isValid() ?? true);
  };

  return (
    <DatePicker
      animateYearScrolling={false}
      className={classes.picker}
      disableFuture={true}
      // This is display format
      format={DATE_FORMATS["DD/MM/YYYY"]}
      InputLabelProps={{ style: { zIndex: 1, pointerEvents: "none" } }}
      invalidDateMessage={t("common:validation.invalid-date-format")}
      keyboard={true}
      label={<DatePickerLabel classes={classes} />}
      mask={mask}
      maxDate={maxDate}
      maxDateMessage={t("common:validation.max-date")}
      minDate={minDate}
      minDateMessage={minDateMessage}
      onChange={handleChangeDate}
      onError={handleDateError}
      placeholder="dd/mm/aaaa"
      value={formState.conciliationStartDate.value}
    />
  );
}

type DatePickerLabelProps = WithStyles<typeof styles>;

function DatePickerLabel({ classes }: DatePickerLabelProps) {
  const { t } = useTranslations();

  return (
    <div className={classes.pickerLabelWrapper}>
      <Typography className={classes.pickerLabel}>
        {t("common:fields.start-date")}
      </Typography>
      <Tooltip
        placement="right"
        title={t("conciliation:herd-conciliation-start-date")}
      >
        <InfoIcon fontSize="inherit" className={classes.labelContextualHelp} />
      </Tooltip>
    </div>
  );
}

type FormButtonProps = {
  formState: ConciliationDateFormState;
  isEditing: boolean;
  onClick: () => void;
} & WithStyles<typeof styles>;

function FormButton({
  classes,
  formState,
  isEditing,
  onClick,
}: FormButtonProps) {
  const { t } = useTranslations();

  const isDisabled =
    formState.conciliationStartDate.value == null ||
    formState.conciliationStartDate.error === true;

  const label = isEditing
    ? t("common:actions.edit-date")
    : t("common:actions.apply");

  return (
    <Button
      className={classes.formButton}
      color="primary"
      data-testid="conciliation-date-form-button"
      disabled={isDisabled}
      onClick={onClick}
      variant="contained"
    >
      {label}
    </Button>
  );
}

const styles = () =>
  createStyles({
    title: {
      fontSize: "20px",
      fontWeight: "bold",
      fontStyle: "medium",
      fontStretch: "medium",
      lineHeight: "medium",
      letterSpacing: "medium",
      color: "#333333",
      marginTop: "40px",
      marginBottom: "20px",
    },
    formContainer: {
      display: "flex",
      height: 80,
    },
    pickerLabelWrapper: {
      bottom: "8px",
      display: "flex",
      fontSize: "16px",
      position: "relative",
    },
    pickerLabel: {
      fontSize: "inherit",
    },
    labelContextualHelp: {
      color: "grey",
      left: "8px",
      pointerEvents: "auto",
      position: "relative",
      top: "2px",
    },
    picker: {
      width: 150,
      marginRight: 16,
    },
    formButton: {
      height: "36px",
      borderRadius: "18px",
      fontFamily: "Roboto",
      fontSize: "14px",
      fontWeight: 500,
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: "normal",
      letterSpacing: "1.25px",
      textAlign: "center",
      color: "#ffffff",
      bottom: "36px",
      minWidth: "140px",
      position: "unset !important" as CSSProperties["position"],
      marginTop: "10px",
    },
  });

export default withStyles(styles)(ConciliationDateForm);
